import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

import { Form, FormControl, FormField, RadioGroup } from '@tg-web/components';
import { useCallbackRef } from '@tg-web/utils';

import { AllowedLang, AllowedLangs } from '../../../shared/lib/useAppLang';
import { useMainButton } from '../../../shared/lib/useMainButton';
import {
  IslandRadioCell,
  IslandSectionHeader,
  islandClassName,
} from '../../../shared/ui/island';

const formSchema = z.object({
  languageCode: z.enum(AllowedLangs),
  theme: z.enum(['dark', 'light', 'system']),
});
type FormType = z.infer<typeof formSchema>;

const langOptions: Array<[AllowedLang, string]> = [
  ['en', 'English'],
  ['pt-BR', 'Português brasileiro'],
  ['fr', 'Français'],
  ['ru', 'Русский'],
  ['de', 'Deutsch'],
  ['zh-CN', '简体中文'],
];

/**
 * `system` was hidden by a909664 and is back by product decision. It follows
 * the Telegram client's own theme, live -- see ThemeProvider. It leads the list
 * per the mock, and no existing user is migrated onto it: the stored value for
 * "never chose" is indistinguishable from an explicit Dark, so it is opt-in.
 */
const colorSchemeOptions: Array<['dark' | 'light' | 'system', string]> = [
  ['system', 'all:settings.appearance.color_options.system'],
  ['light', 'all:settings.appearance.color_options.light'],
  ['dark', 'all:settings.appearance.color_options.dark'],
];

export interface IslandAppearanceSettingsFormProps {
  defaultValues: FormType;
  isLoading: boolean;
  onSubmit: (data: FormType) => void;
}

/**
 * The island counterpart of `AppearanceSettingsForm`. The selection state, what
 * submit sends and what discarding does are unchanged; the one deliberate
 * difference is that Save is Telegram's MainButton rather than a button in the
 * page, wired exactly as the event form wires it.
 */
export function IslandAppearanceSettingsForm({
  defaultValues,
  isLoading,
  onSubmit,
}: IslandAppearanceSettingsFormProps) {
  const { t } = useTranslation();

  const form = useForm<FormType>({
    defaultValues,
    resolver: zodResolver(formSchema),
  });

  const onSubmitCallbackRef = useCallbackRef(onSubmit);

  const handleSubmit = useMemo(
    () => form.handleSubmit((values) => onSubmitCallbackRef(values)),
    [form, onSubmitCallbackRef],
  );

  /*
   * No `isHidden`: this screen has no modal or sheet of its own, so there is
   * nothing for the event form's isSomeModalShown gate to react to. The button
   * is released on unmount by useMainButton's own cleanup.
   *
   * `isLoading` is the whole double-submit guard, and it is Telegram's, not
   * ours: MainButton.showProgress() leaves the button inactive unless it is
   * called with leaveActive, and useMainButton calls it bare.
   *
   * The label re-renders with `t`, so switching language re-runs setText.
   */
  useMainButton({
    isLoading,
    onClick: handleSubmit,
    text: t('all:settings.appearance.button'),
  });

  return (
    <Form {...form}>
      {/*
       * The page is one gap-3 column and this form is a nested one, so the
       * first section header is a :first-child and drops its own 12px top
       * margin. The form carries that 12px instead, which is what puts the
       * same 24px under the page title as every other island screen.
       *
       * pb-6 is the event form's clearance under the MainButton.
       */}
      <form className="mt-3 flex flex-col gap-3 pb-6" onSubmit={handleSubmit}>
        <IslandSectionHeader>
          {t('all:settings.appearance.interface_language')}
        </IslandSectionHeader>
        <FormField
          render={({ field }) => (
            <FormControl>
              {/*
               * The radio group root is the island itself. Wrapping one in the
               * other would put a div between the island and its rows.
               */}
              <RadioGroup
                className={islandClassName}
                defaultValue={field.value}
                onValueChange={field.onChange}
              >
                {langOptions.map(([value, label]) => (
                  <IslandRadioCell key={value} value={value}>
                    {label}
                  </IslandRadioCell>
                ))}
              </RadioGroup>
            </FormControl>
          )}
          control={form.control}
          name="languageCode"
        />
        <IslandSectionHeader>
          {t('all:settings.appearance.color_scheme')}
        </IslandSectionHeader>
        <FormField
          render={({ field }) => (
            <FormControl>
              <RadioGroup
                className={islandClassName}
                defaultValue={field.value}
                onValueChange={field.onChange}
              >
                {colorSchemeOptions.map(([value, label]) => (
                  <IslandRadioCell key={value} value={value}>
                    {t(label)}
                  </IslandRadioCell>
                ))}
              </RadioGroup>
            </FormControl>
          )}
          control={form.control}
          name="theme"
        />
      </form>
    </Form>
  );
}
